// src/components/shared/CourseCard/variants/PlaceholderCard.tsx
import { GripVertical, Edit2, RefreshCw, Trash2, AlertCircle } from 'lucide-react';
import CourseDropdown from '../components/CourseDropdown';
import { getCourseTypeStyle } from '../utils';
import type { UseCourseCardReturn } from '../hooks/useCourseCard';
import { useCreditUnit } from '../../../../contexts/InstitutionConfigContext';

export type PlaceholderCardProps = UseCourseCardReturn;

const PlaceholderCard = ({
  course,
  isDraggable,
  dragBind,
  showActionsMenu,
  toggleDropdown,
  dropdownRef,
  onAction,
  className = '',
  isDragging = false,
}: PlaceholderCardProps) => {
  const { title, credits } = course;
  const creditUnit = useCreditUnit();
  const requirements = (course as { requirements?: { department?: string } }).requirements ?? {};
  const typeStyle = getCourseTypeStyle('placeholder');

  return (
    <div
      data-testid="placeholder-card"
      aria-label={`Placeholder: ${title}`}
      className={`
        relative group rounded-card
        ${typeStyle}
        border-dashed
        transition-all duration-200 ease-out
        hover:shadow-md
        ${showActionsMenu ? 'z-50 shadow-lg' : 'z-0'}
        ${isDragging ? 'opacity-50' : ''}
        ${className}
      `}
      // Same footprint + affordances as UltraCompactCard (owner feedback,
      // #205 prod verify): identical padding + two-row rhythm, always-visible
      // grip handle and actions dropdown. Drag bindings live on the grip
      // ONLY — the body deliberately has no click/drag behavior (all the
      // placeholder's info is already on the compact card, so no expand).
      // The dashed border and icon carry the "not a real course yet" identity.
      style={{ padding: '0.375rem 0.625rem' }}
    >
      <div className="flex items-center gap-2 min-w-0 ml-2 min-h-[28px]">
        {isDraggable && dragBind && (
          <div
            ref={dragBind.ref}
            {...dragBind.attributes}
            {...dragBind.listeners}
            className="flex items-center justify-center cursor-grab active:cursor-grabbing flex-shrink-0 p-1 -ml-1 rounded hover:bg-surface-sunken focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-colors touch-none"
            title="Drag to move — or press Space to lift, arrows to move, Space to drop"
          >
            <GripVertical className="w-3 h-3 text-ink-3 group-hover:text-ink-2 transition-colors" />
          </div>
        )}
        <AlertCircle className="h-3.5 w-3.5 text-slate-500 flex-shrink-0" />
        <span className="font-semibold text-gray-800 text-sm truncate">{title}</span>
        {requirements.department && (
          <>
            <span className="text-gray-400 text-xs">•</span>
            <span className="text-sm text-gray-600 truncate">{requirements.department}</span>
          </>
        )}
        <div className="flex-shrink-0 ml-auto">
          <CourseDropdown
            ref={dropdownRef}
            isOpen={showActionsMenu}
            onToggle={toggleDropdown}
            actions={[]}
            onAction={onAction ?? (() => {})}
            course={course}
            customActions={[
              {
                icon: Edit2,
                label: 'Edit Placeholder',
                action: 'edit',
              },
              {
                icon: RefreshCw,
                label: 'Convert to Course',
                action: 'convert',
              },
              {
                icon: Trash2,
                label: 'Delete',
                action: 'remove',
                className: 'text-red-600 hover:bg-red-50',
              },
            ]}
          />
        </div>
      </div>

      <div className="flex items-center gap-2 ml-2 min-h-[22px] mt-0.5">
        <span className="text-xs text-gray-700 tabular-nums whitespace-nowrap">
          {creditUnit.format(credits)}
        </span>
        <span className="px-2 py-0.5 bg-surface-sunken text-ink-2 text-xs font-medium rounded-full border border-line-soft">
          Placeholder
        </span>
      </div>
    </div>
  );
};

export default PlaceholderCard;
